import { Card, Stack, Title, Text, Group, Skeleton, Button } from '@repletoryreach/sdk-ui';
import type { broadcasts } from '@repletoryreach/sdk-api';
import { useT } from '../../../../i18n/I18nProvider';
import { formatCount } from '../../../../lib/format';
import { formatRate, reasonText } from '../../components/delivery';
import { useMetaBilling } from '../../../../lib/meta-billing';

const MUTED = 'var(--rr-color-text-muted, #94a3b8)';

/**
 * What a broadcast came to (4 October 2026): how many it was handed over for, how many it reached, how many read it
 * (WhatsApp only: SMS and email report delivery, not opening), how many it failed for and why, in words, and who
 * turned the business away after it. "37 failed" used to be the whole story; this says "31 are not on WhatsApp,
 * 6 had no credit" and takes the reader to them.
 */
export function ReportCard({ broadcast, report, loading, onShowRecipients }: {
  broadcast: broadcasts.Broadcast;
  report: broadcasts.BroadcastReport | null;
  loading: boolean;
  onShowRecipients: (status: string) => void;
}) {
  const t = useT();
  // Who Meta bills for the sending number; the broadcast carries the number where the service sends it.
  const metaBilling = useMetaBilling();
  const payer = metaBilling.payerOf((broadcast as { phone_number_id?: string | null }).phone_number_id)
    ?? (metaBilling.onlyOwnAccount ? 'customer' : undefined);

  if (loading && !report) {
    return (
      <Card withBorder>
        <Stack gap="xs"><Skeleton height={20} /><Skeleton height={20} /><Skeleton height={20} /></Stack>
      </Card>
    );
  }
  if (!report) return null;

  const whatsapp = report.channel === 'whatsapp';
  const delivered = report.delivered + report.read;
  const failures = report.failures.filter((f) => f.status === 'failed');
  const leftOut = report.failures.filter((f) => f.status !== 'failed');
  const after = report.opted_out_after;
  const afterParts = [
    after.spam > 0 ? t('broadcasts.report.after.spam', { count: formatCount(after.spam) }) : null,
    after.unsubscribed > 0 ? t('broadcasts.report.after.unsubscribed', { count: formatCount(after.unsubscribed) }) : null,
    after.stop > 0 ? t('broadcasts.report.after.stop', { count: formatCount(after.stop) }) : null
  ].filter((p): p is string => !!p);

  const steps: { key: string; label: string; count: number; rate: number | null; colour: string }[] = [
    { key: 'attempted', label: t('broadcasts.report.attempted'), count: report.attempted, rate: report.attempted > 0 ? 100 : null, colour: 'var(--rr-color-text-muted)' },
    { key: 'delivered', label: t('broadcasts.recipient.delivered'), count: delivered, rate: report.delivered_rate, colour: 'var(--rr-color-primary, #0d9488)' }
  ];
  if (whatsapp) steps.push({ key: 'read', label: t('broadcasts.recipient.read'), count: report.read, rate: report.read_rate, colour: 'var(--rr-color-success, #10b981)' });
  steps.push({ key: 'failed', label: t('status.failed'), count: report.failed, rate: report.failed_rate, colour: 'var(--rr-color-error, #dc2626)' });

  return (
    <Card withBorder data-testid="broadcast-report">
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={4}>{t('broadcasts.report.title')}</Title>
          <Text fz="xs" c={MUTED}>{whatsapp ? t('broadcasts.report.hintWhatsApp') : t('broadcasts.report.hintOther')}</Text>
        </Stack>

        {report.attempted === 0 ? (
          <Text fz="sm" c={MUTED}>{t('broadcasts.report.nothingSent')}</Text>
        ) : (
          <Stack gap={8} role="list" aria-label={t('broadcasts.report.title')}>
            {steps.map((s) => (
              <div key={s.key} role="listitem" data-testid={`report-${s.key}`}>
                <Group justify="space-between" align="baseline" wrap="nowrap" style={{ marginBottom: 2 }}>
                  <Text fz="sm">{s.label}</Text>
                  <Text fz="sm" fw={600}>
                    {formatCount(s.count)}
                    {s.rate != null && s.key !== 'attempted' && <span style={{ color: MUTED, fontWeight: 400 }}>{` · ${formatRate(s.rate)}`}</span>}
                  </Text>
                </Group>
                <div style={{ height: 6, borderRadius: 999, background: 'var(--rr-color-surface-elevated, #1f2937)', overflow: 'hidden' }}>
                  <div style={{ width: `${Math.min(100, s.rate ?? 0)}%`, height: '100%', background: s.colour }} />
                </div>
              </div>
            ))}
          </Stack>
        )}

        {report.awaiting_report > 0 && (
          <Stack gap={2} data-testid="report-awaiting">
            <Text fz="sm">{t('broadcasts.report.awaiting', { count: formatCount(report.awaiting_report) })}</Text>
            <Text fz="xs" c={MUTED}>{t('broadcasts.report.awaitingHint')}</Text>
          </Stack>
        )}

        {failures.length > 0 && (
          <Stack gap={6}>
            <Text fz="sm" fw={600}>{t('broadcasts.report.whyFailed')}</Text>
            {failures.map((f) => (
              <ReasonRow key={`${f.status}-${f.code}`} text={reasonText(t, f.code, f.example, payer)} count={f.count}
                action={t('broadcasts.report.seeWho')} onClick={() => onShowRecipients('failed')} />
            ))}
          </Stack>
        )}

        {leftOut.length > 0 && (
          <Stack gap={6}>
            <Text fz="sm" fw={600}>{t('broadcasts.report.leftOut')}</Text>
            {leftOut.map((f) => (
              <ReasonRow key={`${f.status}-${f.code}`} text={reasonText(t, f.code, f.example, payer)} count={f.count}
                action={t('broadcasts.report.seeWho')} onClick={() => onShowRecipients('filtered_opted_out')} />
            ))}
          </Stack>
        )}

        {afterParts.length > 0 && (
          <Text fz="sm" data-testid="report-after">{t('broadcasts.report.after', { list: afterParts.join(', ') })}</Text>
        )}

        {broadcast.status_code === 'running' && (
          <Text fz="xs" c={MUTED}>{t('broadcasts.report.stillSending')}</Text>
        )}
      </Stack>
    </Card>
  );
}

function ReasonRow({ text, count, action, onClick }: { text: string; count: number; action: string; onClick: () => void }) {
  return (
    <Group justify="space-between" align="center" wrap="nowrap" gap="sm" data-testid="report-reason">
      <Text fz="sm" style={{ minWidth: 0 }}>{text}</Text>
      <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
        <Text fz="sm" fw={600}>{formatCount(count)}</Text>
        <Button size="xs" variant="subtle" onClick={onClick}>{action}</Button>
      </Group>
    </Group>
  );
}
